/* eslint-disable import/no-extraneous-dependencies */
// © 2015-2021 WDNA - All Rights Reserved
import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import Qs from 'qs';

import { messageError } from 'wdna-components/lib/helpers/notification';

import Auth from '../auth/Auth';

// Interceptors
const isoDateFormat: RegExp = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:.[0-9]+)?(?:(?:[Zz])|(\+|-:[0-5][0-9]))$/;

/**
 * Function to check iso RFC399 strings
 *
 * @param value value to check
 * @returns true if is a string whith RFC399 format date
 */
function isIsoDateString(value: any): boolean {
  return (typeof value === 'string' && isoDateFormat.test(value));
}

/**
 *  Function to process date strings from response
 *
 * @param body of the response to process
 * @returns response with Date converted
 */
function handleDates(body: any): any {
  if (body === null || body === undefined || typeof body !== 'object') {
    return body;
  }
  // eslint-disable-next-line no-restricted-syntax
  for (const key of Object.keys(body)) {
    const value: any = body[key];
    if (isIsoDateString(value)) {
      body[key] = new Date(value);
    } else if (typeof value === 'object') handleDates(value);
  }
}

/* eslint no-underscore-dangle: ["error", { "allowAfterThis": true }] */
export default class API {
  private _api: AxiosInstance;

  public constructor(config: AxiosRequestConfig = {}) {
    config.paramsSerializer = this.paramsSerializer;
    this._api = axios.create(config);

    // Interceptor to serialize Date params from response
    this._api.interceptors.response.use((resp: any): any => {
      handleDates(resp.data);
      return resp;
    });
  }

  public get = async <T = any>(path: string, params: any, options?: AxiosRequestConfig): Promise<T> => {
    return this._api.get<T>(path, { params, ...options }).then(this._getResponse<T>).catch(this._getError<T>);
  };

  public post = async <T = any>(path: string, data: any, options?: AxiosRequestConfig): Promise<T> => {
    return this._api.post(path, data, { ...options }).then(this._getResponse<T>).catch(this._getError<T>);
  };

  public put = async <T = any>(path: string, data: any, options?: AxiosRequestConfig): Promise<T> => {
    return this._api.put(path, data, { ...options }).then(this._getResponse<T>).catch(this._getError<T>);
  };

  public patch = async <T = any>(path: string, data: any, options?: AxiosRequestConfig): Promise<T> => {
    return this._api.patch(path, data, { ...options }).then(this._getResponse<T>).catch(this._getError<T>);
  };

  public delete = async <T = any>(path: string, params: any, options?: AxiosRequestConfig): Promise<T> => {
    return this._api.delete(path, { params, ...options }).then(this._getResponse<T>).catch(this._getError<T>);
  };

  public download = async <T = any>(path: string, params: any, options?: AxiosRequestConfig): Promise<T> => {
    return this._api.get(path, { params, ...options, responseType: 'blob' }).then(this._getFile).catch(this._getError);
  };

  private _getResponse = <T = any>(response: any): Promise<T> => {
    const { data } = response;
    if (data.validator) {
      const validatorMessage: string = this._getValidatorMessage(data.validator);
      return this._errorObject(null, validatorMessage);
    }

    return this._responseObject<T>(data);
  };

  private _getValidatorMessage = (validator: Record<string, any>): string => {
    Object.keys(validator).forEach((key: string) => {
      const error: string[] = validator[key];
      if (error.length) return error[0];
    });

    return 'Unknown Error Message';
  };

  private _responseObject = <T = any>(data: any): Promise<T> => {
    return Promise.resolve<T>(data as T);
  };

  private _getFile = (response: any): any => {
    try {
      // Get filename
      const disposition: string = response.headers['content-disposition'];
      const idxIni: number = disposition.indexOf('"') + 1;
      const idxEnd: number = disposition.lastIndexOf('"');
      const filename: string = disposition.substring(idxIni, idxEnd);

      // Create link to download file
      const link: HTMLAnchorElement = document.createElement('a');
      link.href = window.URL.createObjectURL(new Blob([response.data]));
      link.setAttribute('download', filename);
      document.body.appendChild(link);

      link.click();
    } catch (error) {
      console.error(error);
      throw new Error('Unkown Error Downloading File');
    }
  };

  private _getError = <T = any>(error: any): Promise<T> => {
    let status: number = 400;
    let message: string = 'Unknown Error Message';
    if (error.response) {
      ({ status } = error.response);
      message = error.response.data.message ? error.response.data.message : error.response.data;
    } else if (error.request) {
      ({ status } = error.request);
      message = error.request.statusText;
    } else if (error.message && error.message.length > 0) {
      ({ status, message } = error);
    }

    if (status === 401) {
      if (Auth.isUserAuthenticated()) {
        messageError('Authentication error', 'Your session has expired or your credentials have been used recently.', 10000,);
      }
      Auth.deauthenticateUser(true);
    }

    return this._errorObject(status, message);
  };

  private _errorObject = (code: any, message: any): Promise<never> => {
    // eslint-disable-next-line prefer-promise-reject-errors
    return Promise.reject({ code, message });
  };

  public setToken = (token: string): API => {
    this._api.defaults.headers.common.token = token;
    this._api.defaults.headers.common['x-timezone'] = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return this;
  };

  // `paramsSerializer` is an optional function in charge of serializing `params`
  // (e.g. https://www.npmjs.com/package/qs, http://api.jquery.com/jquery.param/)
  public paramsSerializer = (params: any): string => {
    return Qs.stringify(params, { arrayFormat: 'repeat' });
  };
}
